import type { FocusEventHandler, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { Button } from '@/components/atoms/Buttons';
import { CAGE_TYPE_NAMES } from '@/domain/cages/types';
import type { LocationType, OverviewCage } from '@/domain/client';
import { createCagePageUrl } from '@/utils/urlGenerator';

import { penLabelKey } from '../penLabel';

import { useLocationCagePrefetch } from './useLocationCagePrefetch';

import styles from './LocationOverviewCageLinkCell.module.scss';

export interface OpenLinkButtonProps {
    to: string;
    tooltip: string;
    /** What the button reads. Falls back to "Open" when the pen name has its own column. */
    label?: string;
    /** Greys the button out and stops the navigation; the tooltip then says why. */
    disabled?: boolean;
    onMouseEnter?: MouseEventHandler<HTMLAnchorElement>;
    onMouseLeave?: MouseEventHandler<HTMLAnchorElement>;
    onFocus?: FocusEventHandler<HTMLAnchorElement>;
    onBlur?: FocusEventHandler<HTMLAnchorElement>;
}

/**
 * The table's way in.
 *
 * Extracted so every row kind renders the identical control: the container and
 * text classes are what give it its size and alignment inside the cell, and a
 * bare `Button` looks visibly different next to one that has them.
 *
 * The label is a prop rather than a flag read here, so a caller cannot show the
 * pen name in the button while the `Name` column is still showing it too.
 */
export function OpenLinkButton(props: OpenLinkButtonProps) {
    const {
        to,
        tooltip,
        label,
        disabled = false,
        onMouseEnter,
        onMouseLeave,
        onFocus,
        onBlur,
    } = props;
    const { t } = useTranslation();

    return (
        <div
            className={classNames(styles.openButtonContainer, {
                // Only a pen-name button is pinned to a width — see the stylesheet.
                [styles.openButtonContainerNamed]: label != null,
            })}>
            <Button
                tooltip={tooltip}
                variant="table"
                size="small"
                to={to}
                disabled={disabled}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onFocus={onFocus}
                onBlur={onBlur}>
                <div className={styles.openButtonText}>{label ?? t('organisms.common.open')}</div>
            </Button>
        </div>
    );
}

export interface LocationOverviewCageLinkCellProps {
    cage: OverviewCage;
    locationType: LocationType;
    clientId: number;
    locationId: number;
    /** True once the `Name` column is gone and this button carries the pen name. */
    showCageName: boolean;
    /**
     * A pen with no fish group to show. The pen pages send a reader who arrives at one without a
     * group back to the location, so the button does not offer the trip.
     */
    disabled?: boolean;
}

export default function LocationOverviewCageLinkCell(props: LocationOverviewCageLinkCellProps) {
    const { cage, locationType, clientId, locationId, showCageName, disabled = false } = props;
    const { t } = useTranslation();
    const prefetch = useLocationCagePrefetch({
        cageId: cage.cageId,
        fishGroupId: cage.activeFishGroup?.id,
        smolt: String(cage.cageType) === CAGE_TYPE_NAMES.SMOLT_PRODUCTION,
        enabled: !disabled,
    });

    // Carries the pen's cohort for the same reason a departed-cohort row carries
    // its own: every link in this table names the cohort it was showing, so the
    // two row kinds cannot drift apart. With the fish-group flag off
    // `activeFishGroup` is undefined and the URL is the plain one.
    //
    // The tooltip repeats the name the button already shows, on purpose: a long
    // name truncates inside the button, and this is how the reader gets it back.
    return (
        <OpenLinkButton
            to={createCagePageUrl(
                clientId,
                locationId,
                cage.cageId,
                '/dashboard',
                cage.activeFishGroup?.id
            )}
            tooltip={
                disabled
                    ? t('organisms.locationOverviewTable.noFishGroupInPen')
                    : `${t('common.actions.goTo')} ${t(penLabelKey(locationType))} ${cage.cageName}`
            }
            label={showCageName ? cage.cageName : undefined}
            disabled={disabled}
            onMouseEnter={prefetch.onMouseEnter}
            onMouseLeave={prefetch.onMouseLeave}
            onFocus={prefetch.onFocus}
            onBlur={prefetch.onBlur}
        />
    );
}
